import { useContext, useEffect, useRef } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { isObject } from "underscore";

import { PageModuleGroupModules, PageModuleType, Session, TranslateString } from "../../../../types/working-model";
import { sessionPanelCustomPathPattern, sessionPanelPathPattern } from "../../../../utils/admin-routing-utils";
import { SlidingMenuOptionTab } from "./above-the-fold-content";
import AppContext, { AppContexts } from "@/components/app-context";
import { broadcastEvent } from "../../../../utils/event-emitter";

const findActiveModule = (type: string) => (module: PageModuleGroupModules) => {
	return module.type === type && module.is_on;
};

export const shouldDisplayTabs = (tabOptions: SlidingMenuOptionTab[]): boolean => {
	if (tabOptions.length > 1) return true;

	return tabOptions.length === 1 && tabOptions[0].data.uuid !== 'chat';
};

type UseWatchAdminNavType = {
	session: Session | undefined;
	setChatIsActive: (isActive: boolean) => void;
	setSelectedModuleTabId: (tabId: number) => void;
	setSelectedTabUuid: (uuid: string | undefined) => void;
	adminInitialSelectedRouteRef: React.MutableRefObject<string | undefined>;
	adminInitialSelectedTabRef: React.MutableRefObject<number | undefined>;
	tabOptions: SlidingMenuOptionTab[]
}
export const useWatchAdminNav = ({
	session,
	setChatIsActive,
	setSelectedModuleTabId,
	setSelectedTabUuid,
	adminInitialSelectedRouteRef,
	adminInitialSelectedTabRef,
	tabOptions
}: UseWatchAdminNavType) => {
	const location = useLocation();
	const prevLocationRef = useRef(location);
	const appContext = useContext(AppContext);
	const isEditor = appContext === AppContexts.admin;

	// fires on navigate in admin console
	useEffect(() => {
		// when loading the page OR changing routes in the panel,
		// we want to update what the active tab is here so users can see
		// their updates come through in real time, so this is checked on every
		// navigate AND on the initial page load
		const checkAdminPathForTab = async (location: Pick<Location, 'pathname'>) => {
			setTimeout(async () => {
				if (!session?.module_grouping) return;
				const pathUtils = await import('../../../../utils/path-utils');
				const { getSessionPanelRouteState, getExtrasPanelTabRouteState } = pathUtils;

				const {
					isEngage,
					isDetails,
					isEducation,
					isExtras,
					isCourses,
					isSeries
				} = getSessionPanelRouteState(location.pathname);
				const match = matchPath({ path: sessionPanelCustomPathPattern }, window.location.pathname);
				const customPath = (match?.params as unknown as { customPath?: string })?.customPath;
				const isCustom = !!customPath;

				if (tabOptions.length > 1 && (isCustom || isExtras || isEngage || isDetails || isEducation)) {
					setChatIsActive(false);
				}

				// fall through an else-if chain because react router COULD determine that two
				// routes are matches, but we only want to act on one of them
				if (isExtras) {
					setTimeout(() => {
						const {
							isProducts,
							isResources,
							isSuggested,
							isSpeaker,
							isCustomEmbed
						} = getExtrasPanelTabRouteState(location.pathname);
						const extrasModule = session.module_grouping?.find(findActiveModule('extras'));

						if (extrasModule?.uuid) {
							// set the state AND the ref so that the active bar is in the right place
							// the active bar is set async after load so that the DOM measurement is correct
							// we want to preselect the correct tab
							setSelectedTabUuid(extrasModule.uuid);
							adminInitialSelectedRouteRef.current = extrasModule.uuid;

							// these are numbers and I am not certain that the order is going to remain static forever
							// if the sub-tab ordering becomes sortable, we will need to make a change to this
							const [product, resource, suggested, ...customTabs] = extrasModule.modules;

							if (isProducts) {
								adminInitialSelectedTabRef.current = product;
								setSelectedModuleTabId(product);
							} else if (isResources) {
								const historyState = (location as any).state;
								const activeModuleFromHistoryState = isObject(historyState) && historyState?.activeModule;
								const _resource = activeModuleFromHistoryState || resource;
								adminInitialSelectedTabRef.current = _resource;
								setSelectedModuleTabId(_resource);
							} else if (isSuggested) {
								adminInitialSelectedTabRef.current = suggested;
								setSelectedModuleTabId(suggested);
							} else if (isSpeaker) {
								const speakerTab = customTabs.find(tab => session.modules?.find(module => module.id === tab)?.type === PageModuleType.speakers);

								if (speakerTab) {
									adminInitialSelectedTabRef.current = speakerTab;
									setSelectedModuleTabId(speakerTab);
								}
							} else if (isCustomEmbed) {
								const embedTab = customTabs.find(tab => session.modules?.find(module => module.id === tab)?.type === PageModuleType.blank);

								if (embedTab) {
									adminInitialSelectedTabRef.current = embedTab;
									setSelectedModuleTabId(embedTab);
								}
							}
						}
					});
				} else if (isEngage) {
					setTimeout(() => {
						const engageModule = session.module_grouping?.find(findActiveModule('engage'));
						if (engageModule?.uuid) {
							// extract page_module from URL
							const match = matchPath({ path: sessionPanelPathPattern }, location.pathname);
							const pageModuleId = (match?.params as unknown as { page_module?: string })?.page_module;

							// switch the tab
							setSelectedTabUuid(engageModule.uuid);
							adminInitialSelectedRouteRef.current = engageModule.uuid;

							// if we have a page_module, broadcast the expand event after a delay
							if (pageModuleId) {
								const moduleId = Number(pageModuleId);
								// delay to ensure engage components have mounted
								setTimeout(() => {
									broadcastEvent('expand-engage-card', moduleId);
								}, 100);
							}
						}
					});
				} else if (isCourses) {
					setTimeout(() => {
						setSelectedTabUuid('courses');
						adminInitialSelectedRouteRef.current = 'courses';
					});
				} else if (isSeries) {
					setTimeout(() => {
						setSelectedTabUuid('series');
						adminInitialSelectedRouteRef.current = 'series';
					});
				} else if (isCustom) {
					// custom modules embed their path name in this new translateString field "transliterated" 
					// because we don't want to await a transliteration on the attendee side (which we would have to do if we import it)
					setTimeout(() => {
						const customModule = session.module_grouping?.find(module => (module.name as TranslateString).transliterated === customPath);
						if (customModule?.uuid) {
							setSelectedTabUuid(customModule.uuid);
							adminInitialSelectedRouteRef.current = customModule.uuid;
						}
					});
				}
			});
		};

		if (isEditor) {
			const prevLocation = prevLocationRef.current;
			prevLocationRef.current = location;

			// call on mount (when prevLocation === location) and on every navigation
			if (prevLocation === location) {
				// call once on mount
				checkAdminPathForTab(window.location);
			} else {
				checkAdminPathForTab(location);
			}
		}
	}, [isEditor, location, adminInitialSelectedRouteRef, adminInitialSelectedTabRef, session?.module_grouping, setChatIsActive, setSelectedTabUuid, session?.modules, tabOptions.length, setSelectedModuleTabId]);
};
